import React from 'react';
import styled, { keyframes } from 'styled-components';
import { LuFlower2, LuTrees, LuShovel, LuSnowflake } from 'react-icons/lu';
import colors from '../../styles/colors';

const grow = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const ServicesSection = styled.section`
  padding: 120px 40px;
  background: ${colors.background.cream};
  position: relative;

  @media (max-width: 768px) {
    padding: 80px 20px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 70px;
`;

const Subtitle = styled.div`
  color: ${colors.primary.main};
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  color: ${colors.text.heading};
  margin-bottom: 20px;
  font-weight: 800;
`;

const Description = styled.p`
  font-size: 1.15rem;
  color: ${colors.text.body};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.8;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background: ${colors.background.white};
  padding: 45px 40px;
  border-radius: 24px;
  border: 1px solid ${colors.borderLight};
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 5px;
    height: 100%;
    background: ${colors.gradient.gold};
    transform: scaleY(0);
    transition: transform 0.4s ease;
    transform-origin: bottom;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 50px rgba(26, 58, 26, 0.12);

    &::before {
      transform: scaleY(1);
    }
  }
`;

const IconBox = styled.div`
  width: 80px;
  height: 80px;
  background: ${colors.background.light};
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primary.main};
  font-size: 2.2rem;
  margin-bottom: 28px;
  transition: all 0.4s ease;

  ${ServiceCard}:hover & {
    background: ${colors.gradient.gold};
    color: ${colors.primary.dark};
    animation: ${grow} 0.6s ease;
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.4rem;
  color: ${colors.text.heading};
  margin-bottom: 14px;
  font-weight: 700;
`;

const ServiceDesc = styled.p`
  font-size: 1rem;
  color: ${colors.text.body};
  line-height: 1.8;
  margin-bottom: 20px;
`;

const ServiceList = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const ServiceTag = styled.li`
  padding: 8px 16px;
  background: ${colors.background.light};
  color: ${colors.primary.main};
  font-size: 0.85rem;
  font-weight: 600;
  border-radius: 50px;
  transition: all 0.3s ease;

  ${ServiceCard}:hover & {
    background: rgba(201, 162, 39, 0.15);
    color: ${colors.accent.copper};
  }
`;

const services = [
  {
    icon: <LuFlower2 />,
    title: 'Garden Center',
    desc: 'Visit our retail garden center for everything you need to bring your garden to life.',
    tags: ['Plants', 'Flowers', 'Shrubs', 'Mulch', 'Soil'],
  },
  {
    icon: <LuTrees />,
    title: 'Landscape Design',
    desc: 'Custom landscape design and installation to transform your property into a masterpiece.',
    tags: ['Design', 'Installation', 'Hardscaping', 'Patios'],
  },
  {
    icon: <LuShovel />,
    title: 'Lawn Maintenance',
    desc: 'Keep your lawn looking pristine year-round with our professional maintenance services.',
    tags: ['Mowing', 'Fertilizing', 'Edging', 'Aeration'],
  },
  {
    icon: <LuSnowflake />,
    title: 'Snow Removal',
    desc: 'Reliable commercial and residential snow removal to keep you safe all winter long.',
    tags: ['Plowing', 'Salting', 'Sidewalks', '24/7 Service'],
  },
];

export default function Services() {
  return (
    <ServicesSection id="services">
      <Container>
        <Header>
          <Subtitle>What We Do</Subtitle>
          <Title>Our Services</Title>
          <Description>
            From retail garden supplies to full-service landscaping, we have everything
            you need to create and maintain the outdoor space of your dreams.
          </Description>
        </Header>

        <Grid>
          {services.map((service, index) => (
            <ServiceCard key={index}>
              <IconBox>{service.icon}</IconBox>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceDesc>{service.desc}</ServiceDesc>
              <ServiceList>
                {service.tags.map((tag, i) => (
                  <ServiceTag key={i}>{tag}</ServiceTag>
                ))}
              </ServiceList>
            </ServiceCard>
          ))}
        </Grid>
      </Container>
    </ServicesSection>
  );
}
